import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Plus,
  Trash2,
  Shield,
  Search,
  AlertCircle,
  Save,
  X,
  ArrowLeft,
} from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import {
  server_post_data,
  get_all_staff_rights,
  action_staff_right,
  save_update_staff_rights,
} from "../ServiceConnection/serviceconnection";
import {
  handleAphabetsChange,
  handleAlphabetRightId,
} from "../CommonJquery/CommonJquery";

export default function InsidePageRights() {
  const navigate = useNavigate();
  const { id: parentId } = useParams();
  const { toast } = useToast();

  // State management
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [parentRight, setParentRight] = useState(null);
  const [insidePageRights, setInsidePageRights] = useState([]);
  const [filteredRights, setFilteredRights] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedRight, setSelectedRight] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    rightName: "",
    rightClass: "",
  });
  const [errors, setErrors] = useState({});

  // Fetch parent right
  const fetchParentRight = useCallback(async () => {
    if (!parentId) return;

    const fd = new FormData();
    fd.append("call_id", parentId);
    fd.append("flag", "4");

    try {
      const response = await server_post_data(get_all_staff_rights, fd);

      if (!response.data.error && response.data.message.data.length > 0) {
        setParentRight(response.data.message.data[0]);
      }
    } catch (error) {
      console.error("Error fetching parent right:", error);
      toast({
        title: "Error",
        description: "Failed to load parent permission",
        variant: "destructive",
      });
    }
  }, [parentId, toast]);

  // Fetch inside page rights
  const fetchInsidePageRights = useCallback(async () => {
    if (!parentId) return;

    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("call_id", parentId);
    fd.append("flag", "4");

    try {
      const response = await server_post_data(get_all_staff_rights, fd);

      if (!response.data.error) {
        const data = response.data.message.data[0];
        if (data.insidepage) {
          const rightsArray = data.insidepage.split("/").filter((item) => item);
          const parsedRights = rightsArray.map((item, index) => {
            const [rightClass, rightName] = item.split("@");
            return {
              id: index,
              rightClass: rightClass || "",
              rightName: rightName || "",
            };
          });
          setInsidePageRights(parsedRights);
          setFilteredRights(parsedRights);
        } else {
          setInsidePageRights([]);
          setFilteredRights([]);
        }
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching inside page rights:", error);
      toast({
        title: "Error",
        description: "Failed to load inside page rights",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [parentId, toast]);

  // Handle search
  const handleSearch = useCallback(
    (query) => {
      setSearchQuery(query);

      if (!query.trim()) {
        setFilteredRights(insidePageRights);
        return;
      }

      const searchLower = query.toLowerCase();
      const results = insidePageRights.filter(
        (right) =>
          right.rightName?.toLowerCase().includes(searchLower) ||
          right.rightClass?.toLowerCase().includes(searchLower)
      );

      setFilteredRights(results);
    },
    [insidePageRights]
  );

  // Handle field change
  const handleFieldChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const { [field]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  // Open create dialog
  const handleOpenCreateDialog = useCallback(() => {
    setFormData({
      rightName: "",
      rightClass: "",
    });
    setErrors({});
    setIsCreateDialogOpen(true);
  }, []);

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.rightName.trim()) {
      newErrors.rightName = "Right name is required";
    } else if (formData.rightName.trim().length < 3) {
      newErrors.rightName = "Right name must be at least 3 characters";
    }

    if (!formData.rightClass.trim()) {
      newErrors.rightClass = "Right class is required";
    } else if (formData.rightClass.trim().length < 3) {
      newErrors.rightClass = "Right class must be at least 3 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill all required fields",
        variant: "destructive",
      });
      return;
    }

    setShowLoaderAdmin(true);

    // Create new entry
    const newEntry = `${formData.rightClass.trim()}@${formData.rightName.trim()}`;

    // Combine with existing rights
    const existingRights = insidePageRights.map(
      (right) => `${right.rightClass}@${right.rightName}`
    );
    const allRights = [...existingRights, newEntry];
    const joinedString = allRights.join("/");

    const fd = new FormData();
    fd.append("insidepage", joinedString);
    fd.append("parent_id", parentId);
    fd.append("flag", "3");

    try {
      const response = await server_post_data(save_update_staff_rights, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: "Inside page right created successfully",
        });
        setIsCreateDialogOpen(false);
        fetchInsidePageRights();
      }
    } catch (error) {
      console.error("Save error:", error);
      toast({
        title: "Error",
        description: "Failed to save inside page right",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [
    formData,
    parentId,
    insidePageRights,
    validateForm,
    toast,
    fetchInsidePageRights,
  ]);

  // Handle delete click
  const handleDeleteClick = useCallback((right, index) => {
    setSelectedRight(right);
    setSelectedIndex(index);
    setIsDeleteDialogOpen(true);
  }, []);

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (selectedIndex === null) return;

    setShowLoaderAdmin(true);

    // Remove the selected item
    const updatedRights = insidePageRights.filter(
      (_, idx) => idx !== selectedIndex
    );
    const joinedString = updatedRights
      .map((right) => `${right.rightClass}@${right.rightName}`)
      .join("/");

    const fd = new FormData();
    fd.append("id_for_delete", parentId);
    fd.append("flag_for", "2");
    fd.append("for_status_final", joinedString);

    try {
      const response = await server_post_data(action_staff_right, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: "Inside page right deleted successfully",
        });
        fetchInsidePageRights();
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast({
        title: "Error",
        description: "Failed to delete inside page right",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
      setIsDeleteDialogOpen(false);
      setSelectedRight(null);
      setSelectedIndex(null);
    }
  }, [selectedIndex, parentId, insidePageRights, toast, fetchInsidePageRights]);

  // Initial load
  useEffect(() => {
    fetchParentRight();
    fetchInsidePageRights();
  }, [fetchParentRight, fetchInsidePageRights]);

  const stats = {
    total: insidePageRights.length,
  };

  return (
    <MainLayout>
      <Header
        title="Inside Page Rights Management"
        subtitle={
          parentRight
            ? `Manage inside page rights for "${parentRight.page_name}"`
            : "Manage inside page rights"
        }
      />

      <div className="p-6 space-y-6">
        {/* Parent Context */}
        {parentRight && (
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-sm text-muted-foreground">
                    Parent Permission
                  </p>
                  <p className="font-semibold text-foreground">
                    {parentRight.page_name}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate("/view-permissions")}
                className="gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
            </div>
          </div>
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.total}
                </p>
                <p className="text-sm text-muted-foreground">
                  Total Inside Page Rights
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="flex flex-1 gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or class..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            {searchQuery && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery("");
                  setFilteredRights(insidePageRights);
                }}
              >
                Clear
              </Button>
            )}
          </div>
          <Button
            onClick={handleOpenCreateDialog}
            className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
          >
            <Plus className="w-4 h-4" />
            Add Inside Page Right
          </Button>
        </div>

        {/* Inside Page Rights Table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">
                  Right Name
                </TableHead>
                <TableHead className="text-muted-foreground">
                  Right Class
                </TableHead>
                <TableHead className="text-muted-foreground text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRights.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    className="text-center text-muted-foreground"
                  >
                    No inside page rights found
                  </TableCell>
                </TableRow>
              ) : (
                filteredRights.map((right, index) => (
                  <TableRow key={right.id} className="border-border">
                    <TableCell className="font-semibold">
                      {right.rightName}
                    </TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">
                      {right.rightClass}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(right, index)}
                          className="hover:bg-destructive/10 hover:text-destructive"
                          title="Delete Inside Page Right"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Create Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Create New Inside Page Right</DialogTitle>
              <DialogDescription>
                Add a new inside page right to the system
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Right Name */}
              <div className="space-y-2">
                <Label>
                  Right Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  placeholder="e.g., View Details, Edit Settings"
                  value={formData.rightName}
                  onChange={(e) => {
                    const event = {
                      target: e.target,
                      preventDefault: () => {},
                    };
                    handleAphabetsChange(event);
                    handleFieldChange("rightName", e.target.value);
                  }}
                  className={errors.rightName ? "border-destructive" : ""}
                  minLength={3}
                  maxLength={75}
                />
                {errors.rightName && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.rightName}
                  </p>
                )}
              </div>

              {/* Right Class */}
              <div className="space-y-2">
                <Label>
                  Right Class <span className="text-destructive">*</span>
                </Label>
                <Input
                  placeholder="e.g., view-details, edit-settings"
                  value={formData.rightClass}
                  onChange={(e) => {
                    const event = {
                      target: e.target,
                      preventDefault: () => {},
                    };
                    handleAlphabetRightId(event);
                    handleFieldChange("rightClass", e.target.value);
                  }}
                  className={errors.rightClass ? "border-destructive" : ""}
                  minLength={3}
                  maxLength={75}
                />
                {errors.rightClass && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.rightClass}
                  </p>
                )}
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => setIsCreateDialogOpen(false)}
                disabled={showLoaderAdmin}
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={showLoaderAdmin}
                className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
              >
                {showLoaderAdmin ? (
                  "Saving..."
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Create
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <AlertDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Inside Page Right</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete the inside page right{" "}
                <strong>{selectedRight?.rightName}</strong>? This action cannot
                be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MainLayout>
  );
}
